import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
	syncStudentNotes,
	createStudentNote,
	updateStudentNote,
	deleteStudentNote,
	addStudentNoteReply,
	fetchMentionableStaff,
} from '../../../redux/actions/studentNotes';
import { clearStudentNotesError, resetStudentNotesState } from '../../../redux/reducers/studentNotes';
import { Button } from '../../../components/buttons/Button';
import { ButtonIcon } from '../../../components/buttons/ButtonIcon';
import { formatDateTime } from '../../../common/formatDateText';
import { Loading } from '../../../components/loading/Loading';
import { InputTextModern } from '../../../components/inputs/InputTextModern';
import { DropDownFilterSingle } from '../../../components/dropDown/DropDownFilterSingle';
import { PlusIcon, PencilSquareIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { ModalDeleteStudentNote } from './ModalDeleteStudentNote';
import { MentionEditor, MentionReadOnly } from '../../../components/mentions';

const CATEGORIES = [
	{ value: 'control escolar', label: 'Control escolar' },
	{ value: 'pagos', label: 'Pagos' },
	{ value: 'coordinador', label: 'Coordinador' },
	{ value: 'ventas', label: 'Ventas' },
	{ value: 'cobranza', label: 'Cobranza' },
	{ value: 'general', label: 'General' },
];

const STATUSES = [
	{ value: 'abierta', label: 'Abierta' },
	{ value: 'en seguimiento', label: 'En seguimiento' },
	{ value: 'cerrada', label: 'Cerrada' },
];

/** Mensaje del API si faltan título/contenido: no mostrar banner; se valida en cliente y se marca en inputs. */
const SERVER_NOTE_FIELD_VALIDATION_MSG = 'Título y contenido son obligatorios.';

const emptyNoteFieldErrors = () => ({ title: null, content: null });

function fullName(u) {
	if (!u) return 'Usuario';
	return [u.firstName, u.secondName, u.lastName, u.secondSurname].filter(Boolean).join(' ') || 'Usuario';
}

/** Id del usuario que creó la nota (`createdBy` puede ser ObjectId o documento poblado). */
function studentNoteCreatorId(note) {
	const cb = note?.createdBy;
	if (cb == null) return null;
	if (typeof cb === 'object' && cb._id != null) return String(cb._id);
	return String(cb);
}

/** El usuario logueado es el mismo que `createdBy` de la nota (mismo id). */
function loggedUserIsNoteCreator(note, loggedUserId) {
	if (!loggedUserId) return false;
	const creatorId = studentNoteCreatorId(note);
	if (!creatorId) return false;
	return String(loggedUserId) === creatorId;
}

function isNoteClosed(note) {
	return note?.status === 'cerrada';
}

function statusStyles(status) {
	switch (status) {
		case 'abierta':
			return 'bg-sky-50 text-sky-800 ring-sky-200';
		case 'en seguimiento':
			return 'bg-amber-50 text-amber-900 ring-amber-200';
		case 'cerrada':
			return 'bg-gray-100 text-gray-700 ring-gray-200';
		default:
			return 'bg-gray-50 text-gray-700 ring-gray-200';
	}
}

/** Contenido en texto plano para validar y enviar (por si hubiera markup legacy pegado). */
function noteContentPlainForApi(value = '') {
	return String(value)
		.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
		.replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')
		.trim();
}

export const TabStudentNotes = ({
	userId,
	isOpen,
	onStudentNotesFormActiveChange,
	onStudentNotesSaveActionChange,
	onStudentNotesCancelActionChange,
	onStudentNotesFooterSaveLabelChange,
	onStudentNotesFooterLoadingChange,
	onRegisterStudentNotesTabAwayCancel,
	onClearStudentNotesFooter,
	onUserUpdated,
}) => {
	const dispatch = useDispatch();
	const cancelRef = useRef(null);
	const createFormRef = useRef(null);
	const { user } = useSelector((state) => state.user);
	const { userSelected, loadingUserSelected } = useSelector((state) => state.users);
	const {
		loadingSync,
		mutating,
		error: notesError,
		mentionableStaff: staffForMentions,
	} = useSelector((state) => state.studentNotes);

	const notes = userSelected?.studentNotes || [];
	const actorId = user?._id ?? user?.id;

	const [showCreate, setShowCreate] = useState(false);
	const [createForm, setCreateForm] = useState({
		title: '',
		content: '',
		category: 'general',
		status: 'abierta',
	});
	const [editingId, setEditingId] = useState(null);
	const [editForm, setEditForm] = useState({});
	const [replyDrafts, setReplyDrafts] = useState({});
	const [replyTaggedUsers, setReplyTaggedUsers] = useState({});
	const [replyErrors, setReplyErrors] = useState({});
	const [expandedHistories, setExpandedHistories] = useState({});
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [createFieldErrors, setCreateFieldErrors] = useState(emptyNoteFieldErrors);
	const [editFieldErrors, setEditFieldErrors] = useState(emptyNoteFieldErrors);
	const [createTaggedUsers, setCreateTaggedUsers] = useState([]);
	const [editTaggedUsers, setEditTaggedUsers] = useState([]);

	const resetCreateForm = useCallback(() => {
		setCreateForm({
			title: '',
			content: '',
			category: 'general',
			status: 'abierta',
		});
	}, []);

	const handleCancelAllNotes = useCallback(() => {
		setShowCreate(false);
		setEditingId(null);
		setExpandedHistories({});
		setReplyDrafts({});
		resetCreateForm();
		setEditForm({});
		setCreateFieldErrors(emptyNoteFieldErrors());
		setEditFieldErrors(emptyNoteFieldErrors());
		setCreateTaggedUsers([]);
		setEditTaggedUsers([]);
		setReplyTaggedUsers({});
		setReplyErrors({});
	}, [resetCreateForm]);

	useEffect(() => {
		if (!userId) return;
		dispatch(resetStudentNotesState());
		dispatch(syncStudentNotes(userId));
	}, [userId, dispatch]);

	useEffect(() => {
		if (isOpen === false) return;
		if (!userId) return;
		dispatch(fetchMentionableStaff());
	}, [isOpen, userId, dispatch]);

	useEffect(() => {
		if (notesError === SERVER_NOTE_FIELD_VALIDATION_MSG) {
			dispatch(clearStudentNotesError());
		}
	}, [notesError, dispatch]);

	useEffect(() => {
		// Solo cuando el drawer está explícitamente cerrado (`undefined` no cuenta como cerrado).
		if (isOpen === false) {
			handleCancelAllNotes();
			onClearStudentNotesFooter?.();
		}
	}, [isOpen, handleCancelAllNotes, onClearStudentNotesFooter]);

	useEffect(() => {
		if (onRegisterStudentNotesTabAwayCancel) {
			onRegisterStudentNotesTabAwayCancel(() => handleCancelAllNotes);
		}
	}, [handleCancelAllNotes, onRegisterStudentNotesTabAwayCancel]);

	// Al abrir el formulario de "Agregar nota", desplazar suavemente hasta él dentro del scroll de la pestaña.
	useEffect(() => {
		if (!showCreate) return;
		const id = requestAnimationFrame(() => {
			createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
		return () => cancelAnimationFrame(id);
	}, [showCreate]);

	// Si la nota pasa a cerrada (p. ej. otro dispositivo), salir del modo edición.
	useEffect(() => {
		if (!editingId) return;
		const current = notes.find((n) => n._id === editingId);
		if (current && isNoteClosed(current)) {
			setEditingId(null);
			setEditForm({});
			setEditFieldErrors(emptyNoteFieldErrors());
		}
	}, [notes, editingId]);

	useEffect(() => {
		onStudentNotesFormActiveChange?.(Boolean(showCreate || editingId));
	}, [showCreate, editingId, onStudentNotesFormActiveChange]);

	useEffect(() => {
		onStudentNotesFooterLoadingChange?.(mutating);
	}, [mutating, onStudentNotesFooterLoadingChange]);

	useEffect(() => {
		if (!showCreate && !editingId) {
			onStudentNotesFooterSaveLabelChange?.('Guardar nota');
			return;
		}
		onStudentNotesFooterSaveLabelChange?.(showCreate ? 'Guardar nota' : 'Guardar cambios');
	}, [showCreate, editingId, onStudentNotesFooterSaveLabelChange]);

	const handleStudentNotesSave = useCallback(async () => {
		if (!actorId) {
			toast.error('No se pudo identificar al usuario en sesión.');
			return;
		}
		if (showCreate) {
			const titleInvalid = !String(createForm.title || '').trim();
			const contentPlain = noteContentPlainForApi(createForm.content || '');
			const contentInvalid = !String(contentPlain).trim();
			if (titleInvalid || contentInvalid) {
				dispatch(clearStudentNotesError());
				setCreateFieldErrors({
					title: titleInvalid ? 'Este campo es requerido' : null,
					content: contentInvalid ? 'Este campo es requerido' : null,
				});
				return;
			}
			setCreateFieldErrors(emptyNoteFieldErrors());
			const result = await dispatch(
				createStudentNote({
					userId,
					actorId,
					...createForm,
					content: contentPlain,
					taggedUsers: createTaggedUsers,
				})
			);
			if (result?.ok) {
				toast.success('Nota creada');
				setCreateFieldErrors(emptyNoteFieldErrors());
				setShowCreate(false);
				resetCreateForm();
				onUserUpdated?.();
			}
			return;
		}
		if (editingId) {
			const titleInvalid = !String(editForm.title || '').trim();
			const contentPlain = noteContentPlainForApi(editForm.content || '');
			const contentInvalid = !String(contentPlain).trim();
			if (titleInvalid || contentInvalid) {
				dispatch(clearStudentNotesError());
				setEditFieldErrors({
					title: titleInvalid ? 'Este campo es requerido' : null,
					content: contentInvalid ? 'Este campo es requerido' : null,
				});
				return;
			}
			setEditFieldErrors(emptyNoteFieldErrors());
			const result = await dispatch(
				updateStudentNote({
					userId,
					noteId: editingId,
					actorId,
					...editForm,
					content: contentPlain,
					taggedUsers: editTaggedUsers,
				})
			);
			if (result?.ok) {
				toast.success('Nota actualizada');
				setEditFieldErrors(emptyNoteFieldErrors());
				setEditingId(null);
				onUserUpdated?.();
			}
		}
	}, [actorId, showCreate, editingId, createForm, editForm, userId, dispatch, resetCreateForm, onUserUpdated, createTaggedUsers, editTaggedUsers]);

	useEffect(() => {
		if (!showCreate && !editingId) {
			onStudentNotesSaveActionChange?.(null);
			onStudentNotesCancelActionChange?.(null);
			return;
		}
		// El padre guarda el callback en useState: hay que pasar `() => fn` para que React
		// no trate el async handler como actualizador funcional (igual que TabUserData).
		onStudentNotesSaveActionChange?.(() => handleStudentNotesSave);
		onStudentNotesCancelActionChange?.(() => handleCancelAllNotes);
	}, [
		showCreate,
		editingId,
		handleStudentNotesSave,
		handleCancelAllNotes,
		onStudentNotesSaveActionChange,
		onStudentNotesCancelActionChange,
	]);

	const confirmDelete = async () => {
		if (!deleteTarget || !actorId) return;
		if (isNoteClosed(deleteTarget)) {
			toast.error('La nota está cerrada y no se puede eliminar.');
			setDeleteTarget(null);
			return;
		}
		const result = await dispatch(
			deleteStudentNote({ userId, noteId: deleteTarget._id, actorId })
		);
		if (result?.ok) {
			toast.success('Nota eliminada');
			setDeleteTarget(null);
			onUserUpdated?.();
		}
	};

	const handleReply = async (noteId) => {
		const noteForReply = notes.find((n) => n._id === noteId);
		if (noteForReply && isNoteClosed(noteForReply)) {
			toast.error('Esta nota está cerrada; no se pueden añadir comentarios.');
			return;
		}
		const rawText = replyDrafts[noteId] || '';
		const text = noteContentPlainForApi(rawText);
		if (!text) {
			setReplyErrors((prev) => ({ ...prev, [noteId]: 'Este campo es requerido' }));
			return;
		}
		if (!actorId) {
			toast.error('No se pudo identificar al usuario en sesión.');
			return;
		}
		const tagged = replyTaggedUsers[noteId] || [];
		const result = await dispatch(
			addStudentNoteReply({ userId, noteId, actorId, content: text, taggedUsers: tagged })
		);
		if (result?.ok) {
			toast.success('Comentario publicado');
			setReplyDrafts((prev) => ({ ...prev, [noteId]: '' }));
			setReplyTaggedUsers((prev) => ({ ...prev, [noteId]: [] }));
			setReplyErrors((prev) => ({ ...prev, [noteId]: null }));
		}
	};

	const toggleHistory = (noteKey) => {
		setExpandedHistories((prev) => ({
			...prev,
			[noteKey]: !prev[noteKey],
		}));
	};

	const startEdit = (note) => {
		if (isNoteClosed(note)) return;
		setEditFieldErrors(emptyNoteFieldErrors());
		setEditingId(note._id);
		setEditForm({
			title: note.title,
			content: note.content,
			category: note.category,
			status: note.status,
		});
		setEditTaggedUsers(
			Array.from(
				new Set(
					(note.taggedUsers || [])
						.map((u) => (typeof u === 'object' && u?._id ? String(u._id) : String(u)))
						.filter(Boolean)
				)
			)
		);
		setExpandedHistories((prev) => ({ ...prev, [note._id]: false }));
	};

	const blockingLoad =
		loadingSync || (loadingUserSelected && !userSelected?._id);
	// No sustituir toda la pestaña por Loading si ya hay alta/edición en curso (evita que un re-sync borre la UI).
	if (blockingLoad && !showCreate && !editingId) {
		return <Loading />;
	}

	const notesFormOpen = showCreate || Boolean(editingId);

	return (
		<div className="h-full flex flex-col">
			<div className="flex-shrink-0 mb-6">
				<div className="bg-white border border-gray-200 rounded-lg px-5 py-3 lg:py-4 shadow-sm">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-3 min-w-0">
							<div className="min-w-0">
								<h3 className="text-xl font-bold text-gray-900">Notas</h3>
								<p className="text-xs text-gray-500 font-medium mt-0.5 hidden md:block">
									Historial de notas del estudiante.
								</p>
							</div>
						</div>
						{!notesFormOpen && (
							<div className="flex flex-shrink-0 items-center gap-2">
								<Button
									type="button"
									variant="drawerActions"
									onClick={() => {
										setCreateFieldErrors(emptyNoteFieldErrors());
										setShowCreate(true);
									}}
									icon={<PlusIcon className="w-4 h-4 shrink-0" aria-hidden />}
								>
									Agregar
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto pr-2">
				<div className="flex min-h-0 flex-1 flex-col gap-6">
					{notesError && notesError !== SERVER_NOTE_FIELD_VALIDATION_MSG && (
						<div
							className="flex flex-col gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 sm:flex-row sm:items-center sm:justify-between"
							role="alert"
						>
							<span>{notesError}</span>
							<button
								type="button"
								className="self-start text-sm font-semibold text-rose-800 underline sm:self-auto"
								onClick={() => dispatch(clearStudentNotesError())}
							>
								Cerrar
							</button>
						</div>
					)}

					{showCreate && (
						<div
							ref={createFormRef}
							className="scroll-mt-4 rounded-md border border-indigo-100 bg-gray-50 p-5 shadow-sm space-y-4"
						>
							<div className="grid gap-4 sm:grid-cols-2">
								<div className="sm:col-span-2">
									<InputTextModern
										id="student-note-create-title"
										name="title"
										label="Título"
										value={createForm.title}
										onChange={(e) => {
											setCreateForm((f) => ({ ...f, title: e.target.value }));
											setCreateFieldErrors((err) => ({ ...err, title: null }));
										}}
										placeholder="Título de la nota"
										disabled={mutating}
										required
										error={createFieldErrors.title}
									/>
								</div>
								<div className="w-full min-w-0">
									<label className="block text-sm font-semibold text-gray-700 mb-2">Categoría</label>
									<DropDownFilterSingle
										title="Seleccionar categoría"
										addSubTitle={false}
										subTitle="Categoría"
										options={CATEGORIES}
										state={createForm.category}
										setState={(v) => setCreateForm((f) => ({ ...f, category: v }))}
									/>
								</div>
								<div className="w-full min-w-0">
									<label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
									<DropDownFilterSingle
										title="Seleccionar estado"
										addSubTitle={false}
										subTitle="Estado"
										options={STATUSES}
										state={createForm.status}
										setState={(v) => setCreateForm((f) => ({ ...f, status: v }))}
									/>
								</div>
								<div className="sm:col-span-2">
									<MentionEditor
										id="student-note-create-content"
										label="Contenido de la nota"
										required
										value={createForm.content}
										onValueChange={(v) => {
											setCreateForm((f) => ({ ...f, content: v }));
											setCreateFieldErrors((err) => ({ ...err, content: null }));
										}}
										taggedUserIds={createTaggedUsers}
										onTaggedUserIdsChange={setCreateTaggedUsers}
										staffList={staffForMentions}
										placeholder="Detalle de la nota… escribe @ y el nombre para etiquetar"
										disabled={mutating}
										rows={5}
										menuPlacement="auto"
										error={createFieldErrors.content}
									/>
									<p className="mt-1 text-xs text-gray-500">
										Escribe <span className="font-semibold">@</span> (al inicio, tras un espacio o tras un salto de línea) y elige un usuario de la lista.
									</p>
								</div>
							</div>
						</div>
					)}

					{notes.length === 0 && !showCreate ? (
						<div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-4 py-12 text-center">
							<div className="mb-4 rounded-full bg-gray-100 p-6">
								<DocumentTextIcon className="h-16 w-16 text-gray-400" aria-hidden />
							</div>
							<h4 className="mb-2 text-lg font-semibold text-gray-700">No hay notas registradas</h4>
							<p className="max-w-sm px-4 text-sm text-gray-500">
								Aún no se han registrado notas internas para este estudiante.
							</p>
						</div>
					) : (
						<ul className="space-y-4">
							{notes.map((note) => {
								const noteKey = note._id;
								const isExpanded = expandedHistories[noteKey] === true;
								const isEditing = editingId === note._id;
								const historyCount = (note.history || []).length;
								const canEditOrDelete =
									!notesFormOpen &&
									actorId &&
									loggedUserIsNoteCreator(note, actorId) &&
									!isNoteClosed(note);

								return (
									<li
										key={note._id}
										className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-indigo-200"
									>
										{/* Móvil/sm: fila 1 = título + acciones, fila 2 = badges. md+: título, badges y acciones en línea. */}
										<div className="grid w-full grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] gap-x-2 gap-y-2 md:flex md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-x-3 md:gap-y-2">
											<h4
												className={`col-start-1 row-start-1 min-w-0 self-center truncate text-base font-semibold text-gray-900 md:order-1 md:max-w-[min(100%,20rem)] lg:max-w-xs ${
													notesFormOpen || !canEditOrDelete ? 'col-span-2' : ''
												}`}
											>
												{note.title}
											</h4>
											{canEditOrDelete && (
												<div className="col-start-2 row-start-1 flex shrink-0 items-center gap-1 self-center md:order-3">
													<ButtonIcon
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => startEdit(note)}
														disabled={mutating}
														aria-label="Editar nota"
													>
														<PencilSquareIcon className="h-5 w-5 text-indigo-600" aria-hidden />
													</ButtonIcon>
													<ButtonIcon
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => setDeleteTarget(note)}
														disabled={mutating}
														aria-label="Eliminar nota"
													>
														<TrashIcon className="h-5 w-5 text-rose-600" aria-hidden />
													</ButtonIcon>
												</div>
											)}
											<div className="col-span-2 row-start-2 flex min-w-0 flex-wrap items-center gap-2 md:col-span-1 md:row-start-1 md:order-2 md:flex-1 md:gap-2">
												<span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-indigo-100 capitalize">
													{note.category}
												</span>
												<span
													className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize ${statusStyles(note.status)}`}
												>
													{note.status}
												</span>
											</div>
											<p className="col-span-2 text-xs text-gray-500 md:order-4 md:basis-full">
												Última actualización{' '}
												<span className="normal-case tabular-nums">
													{formatDateTime(note.updatedAt || note.createdAt)}
												</span>
												{' · '}
												<span className="capitalize">
													{fullName(note.updatedBy || note.createdBy)}
												</span>
											</p>
										</div>

										{isEditing ? (
											<div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
												<div className="grid gap-4 sm:grid-cols-2">
													<div className="sm:col-span-2">
														<InputTextModern
															id={`student-note-edit-${note._id}-title`}
															name="title"
															label="Título"
															value={editForm.title}
															onChange={(e) => {
																setEditForm((f) => ({ ...f, title: e.target.value }));
																setEditFieldErrors((err) => ({ ...err, title: null }));
															}}
															placeholder="Título de la nota"
															disabled={mutating}
															required
															error={editFieldErrors.title}
														/>
													</div>
													<div className="w-full min-w-0">
														<label className="block text-sm font-semibold text-gray-700 mb-2">Categoría</label>
														<DropDownFilterSingle
															title="Seleccionar categoría"
															addSubTitle={false}
															subTitle="Categoría"
															options={CATEGORIES}
															state={editForm.category}
															setState={(v) => setEditForm((f) => ({ ...f, category: v }))}
														/>
													</div>
													<div className="w-full min-w-0">
														<label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
														<DropDownFilterSingle
															title="Seleccionar estado"
															addSubTitle={false}
															subTitle="Estado"
															options={STATUSES}
															state={editForm.status}
															setState={(v) => setEditForm((f) => ({ ...f, status: v }))}
														/>
													</div>
													<div className="sm:col-span-2">
														<MentionEditor
															id={`student-note-edit-${note._id}-content`}
															label="Contenido de la nota"
															required
															value={editForm.content || ''}
															onValueChange={(v) => {
																setEditForm((f) => ({ ...f, content: v }));
																setEditFieldErrors((err) => ({ ...err, content: null }));
															}}
															taggedUserIds={editTaggedUsers}
															onTaggedUserIdsChange={setEditTaggedUsers}
															staffList={staffForMentions}
															taggedUserDocs={note.taggedUsers}
															placeholder="Detalle… escribe @ y el nombre para etiquetar"
															disabled={mutating}
															rows={5}
															menuPlacement="auto"
															error={editFieldErrors.content}
														/>
														<p className="mt-1 text-xs text-gray-500">
															<span className="font-semibold">@</span> y elige de la lista para etiquetar.
														</p>
													</div>
												</div>
											</div>
										) : (
											<p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">
												<MentionReadOnly
													content={note.content}
													taggedUserIds={note.taggedUsers}
													users={staffForMentions}
													variant="inline"
												/>
											</p>
										)}

										<div className="mt-4 pt-4 border-t border-gray-200">
											<button
												type="button"
												onClick={() => toggleHistory(noteKey)}
												className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
											>
												<div className="flex items-center gap-2 min-w-0">
													<svg
														className="w-5 h-5 text-indigo-600 shrink-0"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														aria-hidden
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
														/>
													</svg>
													<span className="text-sm font-bold text-gray-800 truncate">
														Detalle y comentarios
													</span>
													{historyCount > 0 && (
														<span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full shrink-0">
															{historyCount}
														</span>
													)}
												</div>
												<svg
													className={`w-5 h-5 text-gray-500 transition-transform duration-200 shrink-0 ${
														isExpanded ? 'rotate-180' : ''
													}`}
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													aria-hidden
												>
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
												</svg>
											</button>

											<div
												className={`overflow-hidden transition-all duration-300 ease-in-out ${
													isExpanded ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
												}`}
											>
												<div>
													<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
														Respuestas del equipo
													</p>
													{historyCount === 0 ? (
														<p className="text-sm text-gray-400">Sin comentarios aún.</p>
													) : (
														<ul className="space-y-3">
															{[...(note.history || [])]
																.sort(
																	(a, b) =>
																		new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
																)
																.map((h) => (
																<li
																	key={h._id}
																	className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-gray-800"
																>
																	<p className="whitespace-pre-wrap text-gray-800 text-md">
																		<MentionReadOnly
																			content={h.content}
																			taggedUserIds={h.taggedUsers}
																			users={staffForMentions}
																			variant="inline"
																		/>
																	</p>
																	<p className="mt-1 text-xs text-gray-500">
																		<span className="capitalize">{fullName(h.commentedBy)}</span>
																		<span className="normal-case tabular-nums">
																			{' · '}
																			{formatDateTime(h.createdAt)}
																		</span>
																	</p>
																</li>
																))}
														</ul>
													)}
												</div>
												{!isNoteClosed(note) ? (
													<div className="space-y-2 mt-4">
														<MentionEditor
															id={`student-note-reply-${note._id}-content`}
															label="Agregar comentario"
															required
															value={replyDrafts[note._id] || ''}
															onValueChange={(v) => {
																setReplyDrafts((prev) => ({ ...prev, [note._id]: v }));
																setReplyErrors((prev) => ({ ...prev, [note._id]: null }));
															}}
															taggedUserIds={replyTaggedUsers[note._id] || []}
															onTaggedUserIdsChange={(ids) =>
																setReplyTaggedUsers((prev) => ({ ...prev, [note._id]: ids }))
															}
															staffList={staffForMentions}
															placeholder="Escribe una respuesta… usa @ para etiquetar al personal"
															disabled={mutating}
															rows={3}
															menuPlacement="auto"
															error={replyErrors[note._id] || null}
														/>
														<div className="flex justify-end">
															<Button
																type="button"
																variant="outline"
																className="!py-2"
																loading={mutating}
																onClick={() => handleReply(note._id)}
															>
																Publicar comentario
															</Button>
														</div>
													</div>
												) : (
													<p className="mt-4 text-sm text-gray-500">
														Esta nota está cerrada; no se pueden añadir comentarios.
													</p>
												)}
											</div>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</div>

			<ModalDeleteStudentNote
				open={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={confirmDelete}
				loading={mutating}
				noteTitle={deleteTarget?.title}
				cancelButtonRef={cancelRef}
			/>
		</div>
	);
};
